import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type Prefill = {
  navn: string;
  virksomhed: string;
  branche: string;
  kontakt: string;
};



export function ContactForm() {
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [prefill, setPrefill] = useState<Prefill>({ navn: "", virksomhed: "", branche: "", kontakt: "" });

  useEffect(() => {
    let cancelled = false;
    const apply = (user: { email?: string | null; user_metadata?: Record<string, unknown> } | null) => {
      if (cancelled) return;
      if (!user) {
        setPrefill({ navn: "", virksomhed: "", branche: "", kontakt: "" });
        return;
      }
      const m = (user.user_metadata ?? {}) as Record<string, unknown>;
      const str = (v: unknown) => (typeof v === "string" ? v : "");
      setPrefill({
        navn: str(m.navn) || str(m.full_name) || str(m.name) || "",
        virksomhed: str(m.virksomhed) || str(m.company) || "",
        branche: str(m.branche) || str(m.industry) || "",
        kontakt: str(m.kontakt) || user.email || "",
      });
    };
    supabase.auth.getUser().then(({ data }) => apply(data.user));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => apply(session?.user ?? null));
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);




  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorMessage("");

    const form = e.currentTarget;
    const fd = new FormData(form);
    const payload = {
      navn: String(fd.get("navn") ?? "").trim(),
      virksomhed: String(fd.get("virksomhed") ?? "").trim(),
      branche: String(fd.get("branche") ?? "").trim(),
      kontakt: String(fd.get("kontakt") ?? "").trim(),
      besked: String(fd.get("besked") ?? "").trim(),
    };

    // Validering: kontakt skal være enten gyldig e-mail eller præcis 8 cifre.
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneDigits = payload.kontakt.replace(/[\s\-()+]/g, "");
    const isEmail = emailRe.test(payload.kontakt);
    const isPhone = /^\d{8}$/.test(phoneDigits);
    if (!isEmail && !isPhone) {
      setState("error");
      setErrorMessage("Indtast en gyldig e-mail eller et telefonnummer på 8 cifre.");
      return;
    }
    // Normalisér telefon til rene cifre inden afsendelse.
    if (!isEmail && isPhone) {
      payload.kontakt = phoneDigits;
    }

    setState("sending");
    try {
      const res = await fetch("/api/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Noget gik galt – prøv igen.");
      }
      setState("sent");
      form.reset();
    } catch (err) {
      setState("error");
      setErrorMessage(err instanceof Error ? err.message : "Noget gik galt – prøv igen.");
    }
  }


  const fieldClass =
    "w-full rounded-xl border border-foreground/10 bg-background/40 px-4 py-3 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/40 transition";

  if (state === "sent") {
    return (
      <div className="rounded-2xl bg-primary/10 border border-primary/30 p-8 text-center">
        <div className="mx-auto h-12 w-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-2xl">✓</div>
        <h3 className="mt-4 font-display text-xl font-bold">Tak — vi har modtaget din henvendelse</h3>
        <p className="mt-2 text-muted-foreground">Vi vender tilbage inden for 24 timer.</p>
        <button onClick={() => setState("idle")} className="mt-5 text-sm font-medium text-accent hover:underline">
          Send en ny henvendelse
        </button>
      </div>
    );
  }

  const prefillKey = `${prefill.navn}|${prefill.virksomhed}|${prefill.branche}|${prefill.kontakt}`;

  return (
    <form key={prefillKey} onSubmit={onSubmit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 ml-1">Navn</label>
          <input required name="navn" defaultValue={prefill.navn} maxLength={120} className={fieldClass} placeholder="Dit navn" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 ml-1">Virksomhedsnavn</label>
          <input required name="virksomhed" defaultValue={prefill.virksomhed} maxLength={160} className={fieldClass} placeholder="Fx Hansens VVS" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 ml-1">Branche</label>
        <input required name="branche" defaultValue={prefill.branche} maxLength={120} className={fieldClass} placeholder="Fx VVS, frisør, konsulent…" />
      </div>
      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 ml-1">Telefon eller e-mail</label>
        <input
          required
          name="kontakt"
          defaultValue={prefill.kontakt}
          maxLength={200}
          pattern="([^\s@]+@[^\s@]+\.[^\s@]+)|(\s*\d\s*\d\s*\d\s*\d\s*\d\s*\d\s*\d\s*\d\s*)"
          title="Indtast en gyldig e-mail eller et telefonnummer på 8 cifre"
          className={fieldClass}
          placeholder="dig@firma.dk eller 12345678"
        />

      </div>

      <div>
        <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 ml-1">
          Yderligere information <span className="font-normal normal-case tracking-normal">(valgfri)</span>
        </label>
        <textarea name="besked" rows={3} maxLength={2000} className={fieldClass} placeholder="Har du specifikke ønsker eller relevante detaljer, vi bør kende til?" />
      </div>
      {state === "error" && errorMessage && (
        <p className="text-sm text-destructive">{errorMessage}</p>
      )}
      <button
        type="submit"
        disabled={state === "sending"}
        className="group relative w-full overflow-hidden rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-6 py-4 transition-all shadow-[0_0_30px_-8px_color-mix(in_oklab,var(--primary)_70%,transparent)] hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60"
      >
        <span className="relative z-10">{state === "sending" ? "Sender…" : "Send henvendelse"}</span>
        <div className="absolute inset-0 bg-gradient-to-r from-accent to-highlight opacity-0 group-hover:opacity-100 transition-opacity" />
      </button>
      <p className="text-xs text-center text-muted-foreground">
        Uforpligtende. Vi kontakter dig udelukkende vedrørende din henvendelse.
      </p>
    </form>
  );
}
